import { useMemo } from "react";
import * as d3 from "d3";
import { generateChartData } from "../lib/utils";

type YearRange = [number, number];

export interface DealsFilters {
  industries?: string[];
  ceoGenders?: string[];
  cantons?: string[];
  phases?: string[];
}

export interface ChartFilters {
  timeline: DealsFilters;
  industry: DealsFilters;
  phase: DealsFilters;
  canton: DealsFilters;
  ceoGender: DealsFilters;
}

interface GlobalFilters {
  yearRange: YearRange;
}

const applyChartFilters = (dataset: any[], localFilters?: DealsFilters) => {
  if (!localFilters) return dataset;
  return dataset.filter((item) => {
    if (localFilters.industries?.length && !localFilters.industries.includes(item.Industry))
      return false;
    if (localFilters.phases?.length && !localFilters.phases.includes(item.Phase)) return false;
    if (localFilters.cantons?.length && !localFilters.cantons.includes(item.Canton)) return false;
    if (localFilters.ceoGenders?.length && !localFilters.ceoGenders.includes(item["Gender CEO"]))
      return false;
    return true;
  });
};

export const useChartData = (
  deals: any[],
  globalFilters: GlobalFilters,
  chartFilters: ChartFilters,
  currentHalf?: "H1" | "H2"
) => {
  const baseFilteredDeals = useMemo(() => {
    const yr = globalFilters.yearRange;
    return deals.filter((item) => {
      if (!item.Year) return false;
      if (item.Year < yr[0] || item.Year > yr[1]) return false;
      return true;
    });
  }, [deals, globalFilters.yearRange]);

  const timelineDeals = useMemo(
    () => applyChartFilters(baseFilteredDeals, chartFilters.timeline),
    [baseFilteredDeals, chartFilters.timeline]
  );
  const industryDeals = useMemo(
    () => applyChartFilters(baseFilteredDeals, chartFilters.industry),
    [baseFilteredDeals, chartFilters.industry]
  );
  const phaseDeals = useMemo(
    () => applyChartFilters(baseFilteredDeals, chartFilters.phase),
    [baseFilteredDeals, chartFilters.phase]
  );
  const cantonDeals = useMemo(
    () => applyChartFilters(baseFilteredDeals, chartFilters.canton),
    [baseFilteredDeals, chartFilters.canton]
  );
  const genderDeals = useMemo(
    () => applyChartFilters(baseFilteredDeals, chartFilters.ceoGender),
    [baseFilteredDeals, chartFilters.ceoGender]
  );

  const chartData = useMemo(() => {
    return generateChartData("deals", [], timelineDeals);
  }, [timelineDeals]);

  const previewChartData = useMemo(() => {
    return generateChartData("deals", [], timelineDeals);
  }, [timelineDeals]);

  const previewHideFinalYear = useMemo(() => {
    // Use currentHalf from API: if H1, hide final year in preview; if H2, show it
    // Quarter data is no longer available from API, so we rely solely on currentHalf
    return currentHalf === "H1";
  }, [currentHalf]);

  const timelineSharedYTickCount = 6;
  const analysisSharedYTickCount = 6;

  const yTickValuesVolume = useMemo(() => {
    if (!chartData || !chartData.timeline || !chartData.timeline.length) return null;
    const maxVal = (d3.max(chartData.timeline, (d: any) => d.volume) as number | undefined) || 0;
    if (maxVal === 0) return null;
    return d3
      .scaleLinear()
      .domain([0, maxVal * 1.05])
      .nice()
      .ticks(timelineSharedYTickCount);
  }, [chartData, timelineSharedYTickCount]);

  const yTickValuesCount = useMemo(() => {
    if (!chartData || !chartData.timeline || !chartData.timeline.length) return null;
    const maxVal = (d3.max(chartData.timeline, (d: any) => d.count) as number | undefined) || 0;
    if (maxVal === 0) return null;
    return d3
      .scaleLinear()
      .domain([0, maxVal * 1.05])
      .nice()
      .ticks(timelineSharedYTickCount);
  }, [chartData, timelineSharedYTickCount]);

  const hasTimelineData = chartData?.timeline && chartData.timeline.length > 0;
  const hasIndustryData = industryDeals && industryDeals.length > 0;
  const hasPhaseData = phaseDeals && phaseDeals.length > 0;
  const hasCantonData = cantonDeals && cantonDeals.length > 0;
  const hasGenderData = genderDeals && genderDeals.length > 0;

  return {
    baseFilteredDeals,
    timelineDeals,
    industryDeals,
    phaseDeals,
    cantonDeals,
    genderDeals,
    chartData,
    previewChartData,
    timelineSharedYTickCount,
    analysisSharedYTickCount,
    yTickValuesVolume,
    yTickValuesCount,
    hasTimelineData,
    hasIndustryData,
    hasPhaseData,
    hasCantonData,
    hasGenderData,
    previewHideFinalYear,
  } as const;
};
